/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { reminderTriggerAtom } from '../../../../states/reminder';
import { Box, ButtonBase, IconButton } from '@mui/material';
import QSModal from '../../../../../../../shared/components/molecules/modal';
import { BORDER_COLOR } from '../../../../../../../shared/typings';
import { QSButton, QSTypography } from '../../../../../../../shared/components/atoms';
import CloseIcon from '../../../../../../../shared/icons/icons-revamp/close';
import TimerSimpleIcon from '../../../../../../../shared/icons/icons-revamp/timer_simple';
import { useState } from 'react';
import Loader from '../../../../../../../shared/components/molecules/loader';
import useNotifications from '../../../../../../../shared/hooks/use-notifications';
import { updateReminderStatus } from '../../../../requests/reminder';
import {
  Message,
  MessageOrigin,
  MessageTypesEnum,
  REMINDER_MESSAGE_STATUS,
  ReminderMessage,
  ReminderTriggerMessage,
} from '../../../../typings/message-types';
import { sendMessageToCache } from '../../../../states/cache';
import useConversationNavigate from '../../../../../../../shared/hooks/use-conversation-navigate';
import { AvatarCircle, getInitials, ReminderDetails } from '../../../message-types/reminder-renderer/reminder-details';
import ReminderMessageDetails from '../../../message-types/reminder-renderer/reminder-message';
import { useProfile } from '../../../../../../../shared/hooks/use-profile';
import useWabaLevelPermission from '../../../../../../../shared/hooks/use-waba-level-permission';
import { selectedChatAtom } from '../../../../states/selected-chat';
import { Permissions } from '../../../../../../../shared/typings/user';

export default function ReminderTrigger() {
  const [reminderTrigger, setReminderTrigger] = useAtom(reminderTriggerAtom);
  const {
    reminderId,
    chatMessageId: messageId,
    reminderMessage,
    assigneeDetails,
    dueAt,
    chatId,
    phoneNumber,
    wabaNumber,
    chatName,
    createdBy,
    chatTypeId,
  } = reminderTrigger;
  const [markAsDoneLoading, setMarkAsDoneLoading] = useState(false);
  const { addNotification } = useNotifications();
  const updateCache = useSetAtom(sendMessageToCache);
  const { navigateToConversation } = useConversationNavigate();
  const currentUser = useProfile();
  const selectedChat = useAtomValue(selectedChatAtom);
  const { hasPermission } = useWabaLevelPermission();
  const canCreateReminder = hasPermission(
    selectedChat?.integrationId || '',
    Permissions.CREATE_REMINDERS_SELF
  ).hasAccess;
  const canEditAnyReminder = hasPermission(selectedChat?.integrationId || '', Permissions.EDIT_ANY_REMINDER).hasAccess;
  const canEdit = (currentUser?.id === createdBy && canCreateReminder) || canEditAnyReminder;

  const onClose = () => setReminderTrigger((prev) => ({ ...prev, open: false }));

  const handleMarkAsDoneClick = async () => {
    try {
      setMarkAsDoneLoading(true);
      const response = await updateReminderStatus({
        reminderId,
        status: REMINDER_MESSAGE_STATUS.DISMISSED,
      });

      const reminderMessageToCache: Message = {
        id: messageId || '',
        message: {
          reminderId: reminderId,
          messageType: MessageTypesEnum.REMINDER,
          reminderStatus: REMINDER_MESSAGE_STATUS.DISMISSED,
          reminderMessage: reminderMessage,
          assigneeDetails: assigneeDetails,
          dueAt: dueAt,
          createdBy: createdBy,
        } as ReminderMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: undefined,
        messageTime: 0,
        senderId: '',
        messageOriginType: MessageOrigin.SYSTEM,
        messageMetadata: null,
        readCount: 0,
        senderUser: undefined,
        templateId: null,
        totalCount: 1,
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
        errorMessage: '',
      };

      const reminderTriggerMessage: Message = {
        id: response.reminderTriggerMessageId || '',
        message: {
          reminderId: reminderId,
          messageType: MessageTypesEnum.REMINDER_TRIGGER,
          reminderStatus: REMINDER_MESSAGE_STATUS.DISMISSED,
          reminderMessage: reminderMessage,
          assigneeDetails: assigneeDetails,
          dueAt: dueAt,
          createdBy: createdBy,
        } as ReminderTriggerMessage,
        repliesCount: 0,
        deliveryCount: 0,
        erroredCount: 0,
        sentCount: 0,
        replyMessage: undefined,
        messageTime: 0,
        senderId: '',
        messageOriginType: MessageOrigin.SYSTEM,
        messageMetadata: null,
        readCount: 0,
        senderUser: undefined,
        templateId: null,
        totalCount: 1,
        unreadCount: 0,
        noResponseCount: 0,
        awaitingTemplateApprovalCount: 0,
        errorMessage: '',
      };

      updateCache({
        chatId: chatId || '',
        message: reminderMessageToCache,
        partial: false,
        queue: false,
        fromSocket: true,
      });

      updateCache({
        chatId: chatId || '',
        message: reminderTriggerMessage,
        partial: false,
        queue: false,
        fromSocket: true,
      });

      onClose();
      addNotification({
        message: 'Reminder marked as done',
        type: 'success',
      });
    } catch (error) {
      addNotification({
        message: (error as Error).message || 'Failed to update reminder status',
        type: 'error',
      });
    } finally {
      setMarkAsDoneLoading(false);
    }
  };

  const handleOpenChatClick = () => {
    navigateToConversation({
      phoneNumber: phoneNumber || '',
      waba: wabaNumber || '',
      chatTypeId: chatTypeId,
    });
    onClose();
  };

  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={reminderTrigger.open}
      onClose={onClose}
    >
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          outline: none;
          position: relative;
          width: 480px;
        `}
      >
        <Box
          css={css`
            display: flex;
            height: 55px;
            padding: 12px 16px;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            border-bottom: 1px solid #e0e0ea;
          `}
        >
          <Box
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
            `}
          >
            <AvatarCircle
              styles={css`
                width: 32px;
                height: 32px;
                font-size: 14px;
                color: #317694;
                background: #31769426;
              `}
            >
              {getInitials(chatName)}
            </AvatarCircle>
            <QSTypography
              css={css`
                color: #2d2d2d;
                font-family: Inter;
                font-size: 14px;
                font-style: normal;
                font-weight: 600;
                line-height: normal;
              `}
            >
              {chatName}
            </QSTypography>
          </Box>
          <IconButton
            css={css`
              position: absolute;
              top: 8px;
              right: 8px;
            `}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          css={css`
            display: flex;
            padding: 24px 40px;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 4px;
            `}
          >
            <TimerSimpleIcon size={24} />
            <QSTypography
              css={css`
                color: #df9a30;
                font-family: Inter;
                font-size: 14px;
                font-style: normal;
                font-weight: 600;
                line-height: normal;
                text-transform: uppercase;
              `}
            >
              Reminder
            </QSTypography>
          </Box>

          <Box
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 12px;
            `}
          >
            <QSTypography
              css={css`
                color: #2d2d2d;
                font-family: Inter;
                font-size: 18px;
                font-style: normal;
                font-weight: 500;
                line-height: 150%;
                text-align: center;
              `}
            >
              <ReminderMessageDetails
                reminderMessage={reminderTrigger.reminderMessage}
                assigneeDetails={reminderTrigger.assigneeDetails}
              />
            </QSTypography>

            <ReminderDetails dueAt={reminderTrigger.dueAt} assigneeDetails={reminderTrigger.assigneeDetails} />
          </Box>
        </Box>

        <Box
          css={css`
            display: flex;
            padding: 12px 16px 12px 8px;
            justify-content: space-between;
            align-items: center;
            align-self: stretch;
            border-top: 1px solid ${BORDER_COLOR};
          `}
        >
          {canEdit && (
            <ButtonBase
              css={css`
                display: flex;
                height: 36px;
                padding: 0 16px;
                justify-content: center;
                align-items: center;
                gap: 10px;
                box-shadow: none;

                &:hover {
                  box-shadow: none;
                  background: #7a7a7a1a;
                }
              `}
              onClick={handleMarkAsDoneClick}
              disabled={markAsDoneLoading}
            >
              <QSTypography
                css={css`
                  font-size: 14px;
                  color: #7a7a7a;
                  font-family: Inter;
                  font-weight: 500;
                `}
              >
                Mark as done
              </QSTypography>
              {markAsDoneLoading && (
                <Loader
                  size={16}
                  loaderProps={{
                    sx: {
                      color: '#7a7a7a',
                    },
                  }}
                />
              )}
            </ButtonBase>
          )}

          <QSButton
            css={css`
              display: flex;
              height: 32px;
              padding: 0 12px;
              border-radius: 4px;
              justify-content: center;
              align-items: center;
              gap: 4px;
              box-shadow: none;

              &:hover {
                box-shadow: none;
              }
            `}
            onClick={handleOpenChatClick}
          >
            <QSTypography
              css={css`
                font-size: 14px;
                color: #fff;
                font-family: Inter;
                font-weight: 500;
              `}
            >
              Open Chat
            </QSTypography>
          </QSButton>
        </Box>
      </Box>
    </QSModal>
  );
}
